import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Loader2, Mail, Minus, Plus, ShoppingCart, Star, Tag, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { productImage, isCustomImage, LOGO, DISCORD_URL } from "@/lib/images";
import { SectionLabel, StatusBadge, SaleCountdown } from "@/components/bits";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const TIER_LABELS = { day: "1 DAY", week: "1 WEEK", "15day": "15 DAYS", month: "1 MONTH", "3month": "3 MONTHS", lifetime: "LIFETIME", instant: "INSTANT" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_QTY = 10;

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [tier, setTier] = useState(null);
  const [qty, setQty] = useState(1);
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [email, setEmail] = useState("");
  const [loyalty, setLoyalty] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const cart = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user, email]);

  // returning-buyer / store-credit check once a valid email is entered
  useEffect(() => {
    if (!EMAIL_RE.test(email.trim())) { setLoyalty(null); return; }
    const t = setTimeout(() => {
      api.get(`/checkout/loyalty?email=${encodeURIComponent(email.trim())}`)
        .then((r) => setLoyalty(r.data)).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [email]);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      setTier(r.data.tiers[0]);
    }).catch(() => toast.error("Product not found"));
    api.get(`/reviews?slug=${slug}`).then((r) => setReviews(r.data)).catch(() => {});
  }, [slug]);

  const applyCode = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.get(`/codes/validate?code=${encodeURIComponent(code.trim())}`);
      setApplied(data);
      toast.success(`Code ${data.code} applied — ${data.discount_pct}% off`);
    } catch (e) {
      setApplied(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code");
    }
  };

  const removeCode = () => { setApplied(null); setCode(""); };

  const checkout = async (method) => {
    if (!tier) return;
    if (!user) { toast.error("Log in before checkout"); navigate("/login"); return; }
    if (!user.discord_connected) { toast.error("Connect Discord before checkout"); navigate("/dashboard?discord=required"); return; }
    if (!EMAIL_RE.test(email.trim())) { toast.error("Enter a valid email — your keys are delivered there"); return; }
    if (!agreed) { toast.error("You must accept the Terms of Service to continue"); return; }
    setLoading(method);
    try {
      const { data } = await api.post("/payments/checkout", {
        slug: p.slug, tier: tier.tier, origin_url: window.location.origin,
        email: email.trim(), quantity: qty, method,
        code: applied ? applied.code : null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const status = e.response?.status;
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed");
      if (status === 401) navigate("/login");
      if (status === 403) navigate("/dashboard?discord=required");
      setLoading(false);
    }
  };

  if (!p) return <div className="pt-40 text-center font-mono text-xs tracking-[0.3em] text-white/40">// LOADING...</div>;

  const sale = tier?.sale_price != null;
  const saleTiers = p.tiers.filter((t) => t.sale_price != null);
  const anySale = saleTiers.length > 0;
  const maxSale = anySale ? Math.max(...saleTiers.map((t) => t.sale_pct || 0)) : 0;
  const base = tier ? tier.price : 0;
  const afterSale = tier ? (tier.sale_price ?? tier.price) : 0;
  const discount = applied ? afterSale * (applied.discount_pct / 100) : 0;
  const loyaltyOff = !applied ? afterSale * ((loyalty?.loyalty_pct || 0) / 100) : 0;
  const unitFinal = afterSale - discount - loyaltyOff;
  const finalPrice = (unitFinal * qty).toFixed(2);
  const creditNote = loyalty?.credit_balance > 0 ? Math.min(loyalty.credit_balance, unitFinal * qty) : 0;

  const addToCart = () => {
    cart.add({ slug: p.slug, tier: tier.tier, name: p.name, price: afterSale, image: p.image, quantity: qty });
    toast.success(`${p.name} (${TIER_LABELS[tier.tier] || tier.tier}) added to cart`);
  };

  return (
    <div className="pt-32 pb-28 mx-auto max-w-[1400px] px-5 sm:px-8" data-testid="product-detail-page">
      <Link to="/store" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-white/50 hover:text-white transition-colors mb-10">
        <ArrowLeft size={14} /> BACK TO STORE
      </Link>

      <div className="grid gap-14 lg:grid-cols-2">
        {/* Visual */}
        <div>
          <div className="relative rounded-2xl border border-white/15 p-2 bg-black">
            <div className="relative overflow-hidden rounded-xl">
              <img src={productImage(p.image)} alt={p.name} className="w-full h-[460px] object-cover" style={isCustomImage(p.image) ? undefined : { filter: "grayscale(100%) brightness(42%) contrast(120%)" }} />
              {!isCustomImage(p.image) && (
                <>
                  <div className="absolute inset-0 bg-black/40" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <img src={LOGO} alt="" className="h-28 w-28 object-contain opacity-90" style={{ filter: "drop-shadow(0 0 30px rgba(255,255,255,0.35))" }} />
                    <div className="mt-4 font-display text-2xl uppercase tracking-[0.18em] text-white">{p.game}</div>
                  </div>
                </>
              )}
            </div>
            <div className="absolute top-5 left-5 flex flex-col items-start gap-2">
              <StatusBadge status={p.status} />
              {p.days_undetected > 0 && (
                <span className="font-mono text-[10px] tracking-[0.2em] rounded-full border border-white/25 bg-black/60 px-3 py-1.5 text-white/75" data-testid="streak-badge">
                  {p.days_undetected} DAYS UNDETECTED
                </span>
              )}
            </div>
            {anySale && (
              <div className="absolute top-5 right-5 font-mono text-xs tracking-[0.2em] rounded-full bg-white text-black px-3 py-1.5" data-testid="sale-badge">
                {saleTiers.length === p.tiers.length ? `SALE -${maxSale}%` : `SALE UP TO -${maxSale}%`}
              </div>
            )}
            <div className="absolute bottom-5 right-5 font-mono text-[10px] tracking-[0.3em] text-white/40">// {p.game.toUpperCase()}</div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            {p.features.map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm text-white/70 rounded-xl border border-white/10 px-4 py-3">
                <Check size={14} className="text-white shrink-0" /> {f}
              </div>
            ))}
          </div>
        </div>

        {/* Purchase */}
        <div>
          <SectionLabel>{p.category.toUpperCase()} // {p.game}</SectionLabel>
          <h1 className="mt-4 font-display text-4xl sm:text-6xl uppercase leading-[0.95]">{p.name}</h1>
          <p className="mt-5 text-white/60 leading-relaxed">{p.tagline}</p>

          <div className="mt-10 font-mono text-xs tracking-[0.2em] text-white/40 mb-4">// SELECT DURATION</div>
          <div className="grid grid-cols-2 gap-3" data-testid="tier-selector">
            {p.tiers.map((t) => (
              <button
                key={t.tier}
                onClick={() => setTier(t)}
                data-testid={`tier-${t.tier}`}
                className={`flex items-center justify-between px-5 py-4 rounded-xl border transition-colors ${
                  tier?.tier === t.tier ? "border-white bg-white/[0.06]" : "border-white/15 hover:border-white/40"
                }`}
              >
                <span className="font-mono text-xs tracking-[0.15em]">{TIER_LABELS[t.tier]}</span>
                <span className="text-right">
                  {t.sale_price != null && (
                    <span className="block font-mono text-[10px] text-white/35 line-through leading-none mb-0.5">${t.price.toFixed(2)}</span>
                  )}
                  <span className="font-display text-xl">${(t.sale_price ?? t.price).toFixed(2)}</span>
                </span>
              </button>
            ))}
          </div>

          {/* Quantity */}
          <div className="mt-4 flex items-center justify-between rounded-full border border-white/12 px-5 py-3.5" data-testid="qty-picker">
            <span className="font-mono text-[10px] tracking-[0.2em] text-white/40">// QUANTITY (KEYS)</span>
            <div className="flex items-center gap-4">
              <button onClick={() => setQty(Math.max(1, qty - 1))} disabled={qty <= 1} data-testid="qty-minus"
                className="h-8 w-8 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-white hover:border-white/50 transition-colors disabled:opacity-30">
                <Minus size={14} />
              </button>
              <span className="font-display text-2xl w-8 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(Math.min(MAX_QTY, qty + 1))} disabled={qty >= MAX_QTY} data-testid="qty-plus"
                className="h-8 w-8 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-white hover:border-white/50 transition-colors disabled:opacity-30">
                <Plus size={14} />
              </button>
            </div>
          </div>

          {p.manage_stock && p.stock > 0 && (
            <div className="mt-4 font-mono text-[10px] tracking-[0.2em] text-white/40" data-testid="stock-count">
              // {p.stock} IN STOCK — {qty > p.stock ? `ONLY ${p.stock} READY NOW, THE REST EMAIL WITHIN A FEW HOURS` : "KEYS EMAILED INSTANTLY AFTER PAYMENT"}
            </div>
          )}

          {anySale && p.sale_ends_at && (
            <div className="mt-4"><SaleCountdown endsAt={p.sale_ends_at} tid="detail" /></div>
          )}

          {/* Delivery email */}
          <div className="mt-8">
            <div className="font-mono text-xs tracking-[0.2em] text-white/40 mb-3">// DELIVERY EMAIL (YOUR KEYS ARE SENT HERE)</div>
            <div className="flex items-center gap-3 border border-white/15 px-4 focus-within:border-white transition-colors">
              <Mail size={15} className="text-white/40 shrink-0" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="checkout-email-input"
                className="flex-1 bg-transparent py-3 font-mono text-sm tracking-[0.05em] focus:outline-none placeholder:text-white/25"
              />
            </div>
            {!applied && loyalty?.loyalty_pct > 0 && (
              <p className="mt-2 font-mono text-[10px] tracking-[0.15em] text-white/70" data-testid="returning-chip">
                {loyalty.rank?.pct > (loyalty.returning_pct || 0)
                  ? `// ${loyalty.rank.name} RANK — ${loyalty.loyalty_pct}% OFF EVERY ORDER, AUTO-APPLIED`
                  : `// WELCOME BACK — RETURNING BUYER ${loyalty.loyalty_pct}% OFF AUTO-APPLIED`}
              </p>
            )}
            {loyalty?.credit_balance > 0 && (
              <p className="mt-2 font-mono text-[10px] tracking-[0.15em] text-white/70" data-testid="credit-chip">
                // STORE CREDIT: −${Math.min(loyalty.credit_balance, unitFinal * qty).toFixed(2)} APPLIES AT CHECKOUT
              </p>
            )}
          </div>

          {/* Creator code */}
          <div className="mt-8">
            <div className="font-mono text-xs tracking-[0.2em] text-white/40 mb-3">// CREATOR CODE (OPTIONAL)</div>
            {applied ? (
              <div className="flex items-center justify-between border border-white/40 bg-white/[0.05] px-4 py-3" data-testid="applied-code">
                <span className="flex items-center gap-2 font-mono text-sm"><Tag size={14} /> {applied.code} — {applied.discount_pct}% OFF</span>
                <button onClick={removeCode} data-testid="remove-code-btn" className="text-white/50 hover:text-white"><X size={16} /></button>
              </div>
            ) : (
              <div className="flex gap-3">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="e.g. STAFF5"
                  data-testid="code-input"
                  className="flex-1 bg-transparent border border-white/15 px-4 py-3 font-mono text-sm tracking-[0.15em] focus:border-white focus:outline-none placeholder:text-white/25"
                />
                <button onClick={applyCode} data-testid="apply-code-btn" className="btn-ghost px-6 py-3 text-xs">APPLY</button>
              </div>
            )}
          </div>

          {/* Total + buy */}
          <div className="mt-10 border-t border-white/10 pt-8">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] text-white/40">
                  TOTAL {qty > 1 && <span className="text-white/60">(${unitFinal.toFixed(2)} × {qty})</span>}
                </div>
                {(applied || sale) && <div className="font-mono text-sm text-white/40 line-through">${(base * qty).toFixed(2)}</div>}
                <div className="font-display text-5xl leading-none" data-testid="final-price">${finalPrice}</div>
              </div>
              <div className="flex items-center gap-2">
                {sale && (
                  <span className="font-mono text-[10px] tracking-[0.2em] rounded-full bg-white text-black px-3 py-1.5">SALE -{tier.sale_pct}%</span>
                )}
                <StatusBadge status={p.status} />
              </div>
            </div>
            <label className="flex items-start gap-3 mb-5 cursor-pointer select-none" data-testid="tos-accept-label">
              <input
                type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                data-testid="tos-checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-white"
              />
              <span className="text-xs text-white/55 leading-relaxed">
                I have read and agree to the{" "}
                <Link to="/terms" target="_blank" className="text-white underline">Terms of Service</Link>.
                All sales are digital and delivered to the email above.
              </span>
            </label>
            {p.sold_out && (
              <p className="mb-4 font-mono text-[10px] tracking-[0.15em] text-white/50 border border-white/15 px-4 py-3" data-testid="restocking-note">
                // RESTOCKING — YOU CAN STILL ORDER NOW; YOUR KEYS ARE EMAILED TO YOU AS SOON AS THEY'RE READY
              </p>
            )}
            <div className="space-y-3">
              <button onClick={addToCart} data-testid="add-to-cart-btn" className="w-full py-4 text-xs font-mono tracking-[0.2em] border border-white/40 text-white hover:bg-white hover:text-black transition-colors flex items-center justify-center gap-2">
                <ShoppingCart size={14} /> ADD TO CART{qty > 1 ? ` (${qty})` : ""}
              </button>
              <button onClick={() => checkout("crypto")} disabled={!!loading || !agreed} data-testid="checkout-crypto-btn" className="btn-inj w-full py-5 text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
                {loading === "crypto" ? <><Loader2 size={16} className="animate-spin" /> REDIRECTING...</> : `PAY WITH CRYPTO${qty > 1 ? ` (${qty} KEYS)` : ""} — INSTANT AUTO-DELIVERY`}
              </button>
              <button onClick={() => checkout("manual")} disabled={!!loading || !agreed} data-testid="checkout-manual-btn" className="btn-ghost w-full py-4 text-xs flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
                {loading === "manual" ? <><Loader2 size={16} className="animate-spin" /> REDIRECTING...</> : "VENMO / APPLE PAY"}
              </button>
            </div>
            <p className="mt-4 text-center font-mono text-[10px] tracking-[0.15em] text-white/30">
              CRYPTO: BTC · USDT · LTC & MORE, KEYS AUTO-DELIVERED · VENMO (F&F) / APPLE PAY: CONFIRMED IN MINUTES · <a href={DISCORD_URL} target="_blank" rel="noreferrer" className="underline hover:text-white">NEED HELP?</a>
            </p>
          </div>
        </div>
      </div>

      {/* Buyer reviews — published instantly from post-delivery review links */}
      <section className="mt-20 border-t border-white/10 pt-12" data-testid="product-reviews">
        <SectionLabel>// BUYER REVIEWS</SectionLabel>
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          {reviews?.avg ? (
            <>
              <span className="font-display text-4xl leading-none" data-testid="reviews-avg">{reviews.avg}</span>
              <span className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} size={16} className={n <= Math.round(reviews.avg) ? "fill-white text-white" : "text-white/20"} />
                ))}
              </span>
              <span className="font-mono text-[10px] tracking-[0.2em] text-white/40">{reviews.count} VERIFIED {reviews.count === 1 ? "PURCHASE" : "PURCHASES"}</span>
            </>
          ) : (
            <span className="font-mono text-[10px] tracking-[0.2em] text-white/40" data-testid="reviews-empty">
              NO REVIEWS YET — EVERY BUYER GETS A REVIEW LINK WHEN THEIR KEY DELIVERS
            </span>
          )}
        </div>
        {reviews?.reviews?.length > 0 && (
          <div className="mt-8 grid gap-3 md:grid-cols-2">
            {reviews.reviews.map((r, i) => (
              <article key={i} className="rounded-2xl border border-white/10 px-5 py-4 hover:border-white/25 transition-colors" data-testid={`review-card-${i}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={13} className={n <= r.rating ? "fill-white text-white" : "text-white/20"} />
                    ))}
                  </span>
                  <span className="font-mono text-[9px] tracking-[0.1em] text-white/30">
                    {(() => { try { return new Date(r.created_at).toLocaleDateString(); } catch { return ""; } })()}
                  </span>
                </div>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">{r.text}</p>
                <div className="mt-4 font-mono text-[10px] tracking-[0.15em] text-white/40 uppercase">
                  {r.author} {r.author_og && <span className="border border-white/25 bg-white/5 px-1.5 py-0.5 text-[8px] text-white/80 ml-1" data-testid="review-og-badge">SERVER OG</span>} <span className="text-white/25">· VERIFIED PURCHASE</span>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
